import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Close as CloseIcon,
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import ClienteSearch from '../../components/Tesoreria/ClienteSearch';
import LocalidadSearch from '../../components/LocalidadSearch';
import CatalogoCrudDialog from '../../components/servicio/CatalogoCrudDialog';
import UnidadCrudDialog from '../../components/servicio/UnidadCrudDialog';
import VendedorSelect from '../../components/VendedorSelect';
import clienteService, { OPTIONS_CLIENTE_OPERATIVO } from '../../services/clienteService';
import { resolverNombreCliente } from '../facturaventa/pos/clienteNombre';
import estrategiaService from '../../services/estrategiaService';
import turnoService, {
  CreateTurnoDto,
  ESTADOS_TURNO,
  EstadoTurno,
  LABEL_ESTADO_TURNO,
  TipoClienteTurno,
  TurnoServicioDto,
} from '../../services/turnoService';
import { facturaPosPath, ROUTES } from '../../config/routes';
import { BORDER_ESTADO_TURNO, COLOR_ESTADO_TURNO } from '../../utils/turnoHorario';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

interface LocalidadSel {
  codigoCiudad: number;
  nombreCiudad?: string;
  nombreProvincia?: string;
}

const extractApiError = (e: unknown, fallback: string): string => {
  const err = e as { response?: { data?: unknown }; message?: string };
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (typeof data === 'string') return data;
  const d = data as Record<string, unknown>;
  if (d.message) return String(d.message);
  if (d.errors && typeof d.errors === 'object') {
    const general = (d.errors as { general?: string[] }).general;
    if (general?.[0]) return general[0];
    return Object.values(d.errors as Record<string, string[]>)
      .flat()
      .join(' | ');
  }
  return fallback;
};

/** Alta mínima de cliente (API real) con los mismos defaults que el POS. */
const crearClienteDesdeTurno = async (params: {
  nombre: string;
  telefono: string;
  direccion: string;
  codigoCiudad: number;
  nroVendedor: number | null;
}): Promise<{ nroCliente: number; nombre: string; telefono: string }> => {
  const [proximo, estrategia] = await Promise.all([
    clienteService.getProximoNumero(),
    estrategiaService.getEstrategia(1).catch(() => null),
  ]);
  const nroCliente = Number(proximo) || 0;
  const nroCuenta =
    estrategia?.clientes != null && Number(estrategia.clientes) > 0
      ? Number(estrategia.clientes)
      : null;

  if (!nroCliente || nroCliente <= 0) {
    throw new Error('No hay número de cliente disponible');
  }
  if (!nroCuenta || nroCuenta <= 0) {
    throw new Error(
      'No se encontró cuenta contable por defecto (estrategia). Configurala o das de alta el cliente desde Clientes.'
    );
  }

  const nombreTrim = params.nombre.trim();
  const hoy = new Date().toISOString().split('T')[0];
  const vendedor = params.nroVendedor && params.nroVendedor > 0 ? params.nroVendedor : 1;

  const creado = await clienteService.createCliente({
    nroCliente,
    nombre: nombreTrim,
    razonSocial: nombreTrim,
    direccion: params.direccion.trim(),
    codigoCiudad: params.codigoCiudad,
    cuit: null,
    dni: null,
    tipoIva: 'C',
    condVta: 'CONTADO',
    tipoCliente: 'A',
    nroCuenta,
    fechaAlta: hoy,
    vendedor,
    nroCobrador: 1,
    codigoDireccion: 'FISCAL',
    codigoRuta: 1,
    deposito: 0,
    telefono1: params.telefono.trim() || null,
    email: null,
    credito: 0,
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });

  return {
    nroCliente: Number(creado.nroCliente) || nroCliente,
    nombre: creado.nombre || creado.razonSocial || nombreTrim,
    telefono: creado.telefono1 || params.telefono.trim() || '',
  };
};

const DURACION_MAX_MIN = 12 * 60; // personalizada hasta 12 h
/** Atajos rápidos; si la duración no coincide, quedan deseleccionados. */
const DURACION_PRESETS = [
  { min: 15, label: '15 min' },
  { min: 30, label: '30 min' },
  { min: 45, label: '45 min' },
  { min: 60, label: '1 h' },
  { min: 90, label: '1 h 30' },
  { min: 120, label: '2 h' },
] as const;

const todayIsoDate = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isFechaAnteriorAHoy = (fechaIso: string): boolean => {
  const f = fechaIso.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f)) return true;
  return f < todayIsoDate();
};

const timeToMinutes = (hora: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hora.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours > 23 || minutes > 59) {
    return null;
  }
  return hours * 60 + minutes;
};

const formatHoraFinal = (horaInicio: string, duracionMinutos: number): string => {
  const start = timeToMinutes(horaInicio);
  if (start == null) return '—';
  const total = start + Math.max(0, duracionMinutos);
  const endH = Math.floor(total / 60) % 24;
  const endM = total % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
};

/**
 * Horario del turno:
 * atajos + duración editable + desde/hasta (Hasta actualiza la duración).
 * Si la duración no coincide con un atajo, los botones quedan deseleccionados.
 */
const HorarioTurno: React.FC<{
  horaInicio: string;
  onHoraInicioChange: (hora: string) => void;
  duracionMinutos: number;
  onDuracionChange: (minutos: number) => void;
  horaFinal: string;
  disabled?: boolean;
}> = ({
  horaInicio,
  onHoraInicioChange,
  duracionMinutos,
  onDuracionChange,
  horaFinal,
  disabled = false,
}) => {
  const clamped = Math.min(DURACION_MAX_MIN, Math.max(0, duracionMinutos));
  const esPreset = DURACION_PRESETS.some((p) => p.min === clamped);
  const [hastaDraft, setHastaDraft] = useState<string | null>(null);
  const hastaValue = hastaDraft ?? (horaFinal === '—' ? '' : horaFinal);

  const handleDuracionManual = (raw: string) => {
    if (disabled) return;
    if (raw.trim() === '') {
      onDuracionChange(0);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    onDuracionChange(Math.min(DURACION_MAX_MIN, Math.max(0, Math.round(n))));
  };

  const aplicarFin = (fin: string) => {
    if (disabled) return;
    const start = timeToMinutes(horaInicio);
    const end = timeToMinutes(fin);
    if (start == null || end == null) return;
    const diff = end - start;
    if (diff < 1) return;
    onDuracionChange(Math.min(DURACION_MAX_MIN, diff));
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        bgcolor: '#fafbfc',
        border: '1px solid',
        borderColor: 'divider',
        opacity: disabled ? 0.75 : 1,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25, color: 'text.primary' }}>
        Horario del turno
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
        {disabled ? 'Solo consulta' : 'Elegí un atajo o editá la duración / el horario'}
      </Typography>

      <Box
        role="group"
        aria-label="Duración aproximada"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(6, 1fr)' },
          gap: 1,
          mb: 2,
        }}
      >
        {DURACION_PRESETS.map((p) => {
          const selected = clamped === p.min;
          return (
            <Button
              key={p.min}
              size="small"
              variant={selected ? 'contained' : 'outlined'}
              color="primary"
              disabled={disabled}
              onClick={() => onDuracionChange(p.min)}
              aria-pressed={selected}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                py: 1,
                minWidth: 0,
                px: 0.5,
                boxShadow: selected ? '0 1px 4px rgba(25, 118, 210, 0.35)' : 'none',
                bgcolor: selected ? undefined : '#fff',
                opacity: !esPreset && clamped > 0 ? 0.7 : 1,
              }}
            >
              {p.label}
            </Button>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
          gap: 1.5,
          alignItems: 'center',
        }}
      >
        <TextField
          label="Desde"
          type="time"
          size="small"
          value={horaInicio}
          disabled={disabled}
          onChange={(e) => onHoraInicioChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' },
            '& input': { fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
          }}
        />

        <TextField
          label="Duración"
          type="number"
          size="small"
          value={clamped || ''}
          disabled={disabled}
          onChange={(e) => handleDuracionManual(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: 0, max: DURACION_MAX_MIN, step: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary">
                  min
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', sm: 132 },
            justifySelf: 'center',
            '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' },
            '& input': {
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'center',
            },
          }}
        />

        <TextField
          label="Hasta"
          type="time"
          size="small"
          value={hastaValue}
          disabled={disabled}
          onFocus={() => {
            if (!disabled) setHastaDraft(horaFinal === '—' ? '' : horaFinal);
          }}
          onChange={(e) => {
            setHastaDraft(e.target.value);
            aplicarFin(e.target.value);
          }}
          onBlur={() => setHastaDraft(null)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' },
            '& input': { fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
          }}
        />
      </Box>
    </Box>
  );
};

const toDateInput = (value?: string | null): string => {
  if (!value) return todayIsoDate();
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return todayIsoDate();
  return d.toISOString().slice(0, 10);
};

const toTimeInput = (value?: string | null): string => {
  if (!value) return '10:00';
  const s = String(value).trim();
  const match = /^(\d{1,2}):(\d{2})/.exec(s);
  if (match) return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
  return '10:00';
};

type NuevoTurnoLocationState = {
  fecha?: string;
  horaInicio?: string;
};

const TurnoForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { id: idParam } = useParams();
  const idDecoded = idParam ? decodeURIComponent(idParam) : '';
  const isEdit = !!(idParam && idParam !== 'nuevo');
  const formRef = useRef<HTMLFormElement>(null);
  const nuevoState = (!isEdit ? location.state : null) as NuevoTurnoLocationState | null;

  const [fecha, setFecha] = useState(() => {
    const f = nuevoState?.fecha?.trim();
    if (f && /^\d{4}-\d{2}-\d{2}/.test(f)) return f.slice(0, 10);
    return todayIsoDate();
  });
  const [tipoCliente, setTipoCliente] = useState<TipoClienteTurno>('Habitual');
  const [anulado, setAnulado] = useState(false);
  const [nroCliente, setNroCliente] = useState<number | null>(null);
  const [nombreCliente, setNombreCliente] = useState('');
  const [telefonoCliente, setTelefonoCliente] = useState('');
  const [direccionCliente, setDireccionCliente] = useState('');
  const [localidadCliente, setLocalidadCliente] = useState<LocalidadSel | null>(null);
  const [openLocalidadSearch, setOpenLocalidadSearch] = useState(false);
  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [codigoServicio, setCodigoServicio] = useState('');
  const [patente, setPatente] = useState('');
  const [horaInicio, setHoraInicio] = useState(() =>
    nuevoState?.horaInicio ? toTimeInput(nuevoState.horaInicio) : '10:00'
  );
  const [duracionMinutos, setDuracionMinutos] = useState(15);
  const [nota, setNota] = useState('');
  const [estado, setEstado] = useState<EstadoTurno>('PROGRAMADO');
  const [nroFormularioOt, setNroFormularioOt] = useState<number | null>(null);
  const [generandoOt, setGenerandoOt] = useState(false);

  const [servicios, setServicios] = useState<TurnoServicioDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingServicios, setLoadingServicios] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openClienteSearch, setOpenClienteSearch] = useState(false);
  const [openServiciosCrud, setOpenServiciosCrud] = useState(false);
  const [openUnidadSearch, setOpenUnidadSearch] = useState(false);

  const horaFinal = useMemo(
    () => formatHoraFinal(horaInicio, duracionMinutos),
    [horaInicio, duracionMinutos]
  );

  const dialogsOpen = openClienteSearch || openLocalidadSearch || openServiciosCrud || openUnidadSearch;
  const soloLectura = estado === 'FACTURADO';

  const clienteDisplay =
    nroCliente != null
      ? `${nroCliente}${nombreCliente ? ` - ${nombreCliente}` : ''}`
      : '';

  const localidadLabel = localidadCliente
    ? [localidadCliente.nombreCiudad, localidadCliente.nombreProvincia].filter(Boolean).join(', ')
    : '';

  const loadServicios = useCallback(async () => {
    try {
      setLoadingServicios(true);
      const items = await turnoService.getServicios();
      setServicios(items);
    } catch {
      setServicios([]);
    } finally {
      setLoadingServicios(false);
    }
  }, []);

  const handleCloseServiciosCrud = useCallback(() => {
    setOpenServiciosCrud(false);
    void loadServicios();
  }, [loadServicios]);

  useEffect(() => {
    loadServicios();
  }, [loadServicios]);

  const loadTurno = useCallback(async () => {
    if (!isEdit || !idDecoded) return;
    try {
      setLoading(true);
      const t = await turnoService.getTurno(idDecoded);
      setFecha(toDateInput(t.fecha));
      setTipoCliente(t.tipoCliente || 'Habitual');
      setAnulado(Boolean(t.anulado));
      setNroCliente(t.nroCliente);
      setNombreCliente(t.nombreCliente || '');
      setTelefonoCliente(t.telefonoCliente || '');
      setNroVendedor(t.nroVendedor);
      setCodigoServicio(t.codigoServicio || '');
      setPatente(t.patente || '');
      setHoraInicio(toTimeInput(t.horaInicio));
      setDuracionMinutos(t.duracionMinutos || 15);
      setNota(t.nota || '');
      setEstado(t.estado || 'PROGRAMADO');
      setNroFormularioOt(t.nroFormulario && t.nroFormulario > 0 ? t.nroFormulario : null);
    } catch {
      enqueueSnackbar('Error al cargar el turno', { variant: 'error' });
      navigate('/servicio/turnos');
    } finally {
      setLoading(false);
    }
  }, [isEdit, idDecoded, enqueueSnackbar, navigate]);

  const handleGenerarOt = useCallback(async () => {
    if (!isEdit || !idDecoded) {
      enqueueSnackbar('Guardá el turno antes de generar la orden de trabajo', { variant: 'warning' });
      return;
    }
    if (!nroCliente || nroCliente <= 0) {
      enqueueSnackbar('El turno debe tener un cliente habitual', { variant: 'warning' });
      return;
    }
    try {
      setGenerandoOt(true);
      const updated = await turnoService.generarOrdenTrabajo(idDecoded);
      const nroOt = updated.nroFormulario ?? null;
      setNroFormularioOt(nroOt);
      setEstado(updated.estado || 'EN_TRABAJO');
      enqueueSnackbar('Orden de trabajo generada', { variant: 'success' });
      if (nroOt) navigate(ROUTES.servicio.ordenesTrabajo.editar(nroOt));
    } catch (error: unknown) {
      enqueueSnackbar(extractApiError(error, 'Error al generar la orden de trabajo'), {
        variant: 'error',
      });
    } finally {
      setGenerandoOt(false);
    }
  }, [isEdit, idDecoded, nroCliente, enqueueSnackbar, navigate]);

  const handleRegistrarSena = useCallback(() => {
    if (!nroCliente || nroCliente <= 0) {
      enqueueSnackbar('Seleccioná un cliente habitual para registrar la seña', { variant: 'warning' });
      return;
    }
    const detalle = [
      'Seña servicio',
      idDecoded ? `turno #${idDecoded}` : null,
      nroFormularioOt ? `OT #${nroFormularioOt}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    navigate(ROUTES.tesoreria.recibos.nuevoConCliente(nroCliente, { detalle }));
  }, [nroCliente, idDecoded, nroFormularioOt, enqueueSnackbar, navigate]);

  const handleFacturarOt = useCallback(() => {
    if (!nroFormularioOt || nroFormularioOt <= 0) {
      enqueueSnackbar('Generá primero la orden de trabajo', { variant: 'warning' });
      return;
    }
    navigate(facturaPosPath({ ordenTrabajoId: nroFormularioOt }));
  }, [nroFormularioOt, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) loadTurno();
  }, [isEdit, loadTurno]);

  const handleClienteSearchSelect = async (cliente: {
    nroCliente: number;
    nombre: string;
    razonSocial?: string;
    telefono1?: string;
  }) => {
    const nro = cliente.nroCliente;
    setNroCliente(nro);
    setNombreCliente(resolverNombreCliente(cliente));
    setTelefonoCliente(cliente.telefono1 || '');
    setTipoCliente('Habitual');
    setOpenClienteSearch(false);
    try {
      const c = await clienteService.getCliente(nro, OPTIONS_CLIENTE_OPERATIVO);
      const nombre = resolverNombreCliente(c);
      if (nombre) setNombreCliente(nombre);
      const tel = (c.telefono1 || '').trim();
      if (tel) setTelefonoCliente(tel);
    } catch {
    }
  };

  const handleCancel = useCallback(() => {
    if (saving) return;
    navigate('/servicio/turnos');
  }, [navigate, saving]);

  const buildPayload = (
    overrides?: Partial<Pick<CreateTurnoDto, 'nroCliente' | 'nombreCliente' | 'telefonoCliente' | 'tipoCliente'>>
  ): CreateTurnoDto => ({
    fecha,
    tipoCliente: overrides?.tipoCliente ?? tipoCliente,
    anulado: isEdit ? anulado : false,
    nroCliente:
      overrides?.nroCliente !== undefined
        ? overrides.nroCliente
        : tipoCliente === 'Habitual'
          ? nroCliente
          : null,
    nombreCliente: overrides?.nombreCliente ?? (nombreCliente.trim() || null),
    telefonoCliente: overrides?.telefonoCliente ?? (telefonoCliente.trim() || null),
    nroVendedor,
    codigoServicio: codigoServicio.trim(),
    horaInicio,
    duracionMinutos,
    nota: nota.trim() || null,
    patente: patente.trim() || null,
    estado,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || soloLectura) return;
    try {
      setSaving(true);

      if (!fecha) {
        enqueueSnackbar('La fecha es requerida', { variant: 'warning' });
        return;
      }
      if (isFechaAnteriorAHoy(fecha)) {
        enqueueSnackbar('La fecha no puede ser anterior a hoy', { variant: 'warning' });
        return;
      }
      if (tipoCliente === 'Habitual' && !nroCliente) {
        enqueueSnackbar('Seleccioná un cliente', { variant: 'warning' });
        return;
      }
      if (tipoCliente === 'Nuevo') {
        if (!nombreCliente.trim()) {
          enqueueSnackbar('El nombre del cliente es requerido', { variant: 'warning' });
          return;
        }
        if (!direccionCliente.trim()) {
          enqueueSnackbar('La dirección del cliente es requerida', { variant: 'warning' });
          return;
        }
        if (!localidadCliente?.codigoCiudad) {
          enqueueSnackbar('Seleccioná la ciudad del cliente', { variant: 'warning' });
          return;
        }
      }
      if (!codigoServicio.trim()) {
        enqueueSnackbar('El servicio es requerido', { variant: 'warning' });
        return;
      }
      if (!horaInicio) {
        enqueueSnackbar('La hora de inicio es requerida', { variant: 'warning' });
        return;
      }
      if (duracionMinutos <= 0) {
        enqueueSnackbar('La duración debe ser mayor a 0', { variant: 'warning' });
        return;
      }
      if (estado === 'EN_TRABAJO' && !(nroFormularioOt && nroFormularioOt > 0)) {
        enqueueSnackbar('Generá la orden de trabajo antes de pasar a En trabajo', {
          variant: 'warning',
        });
        return;
      }

      let payload = buildPayload();

      if (tipoCliente === 'Nuevo' && !isEdit) {
        try {
          const creado = await crearClienteDesdeTurno({
            nombre: nombreCliente,
            telefono: telefonoCliente,
            direccion: direccionCliente,
            codigoCiudad: localidadCliente!.codigoCiudad,
            nroVendedor,
          });
          setNroCliente(creado.nroCliente);
          setNombreCliente(creado.nombre);
          setTelefonoCliente(creado.telefono);
          payload = buildPayload({
            tipoCliente: 'Nuevo',
            nroCliente: creado.nroCliente,
            nombreCliente: creado.nombre,
            telefonoCliente: creado.telefono || null,
          });
          enqueueSnackbar(`Cliente #${creado.nroCliente} creado`, { variant: 'success' });
        } catch (clienteErr: unknown) {
          enqueueSnackbar(
            extractApiError(clienteErr, 'Error al crear el cliente'),
            { variant: 'error' }
          );
          return;
        }
      }

      if (isEdit) {
        await turnoService.updateTurno(idDecoded, payload);
      } else {
        await turnoService.createTurno(payload);
      }
      enqueueSnackbar('Turno guardado exitosamente', { variant: 'success' });
      navigate('/servicio/turnos');
    } catch (error: unknown) {
      console.error('Error al guardar turno:', error);
      enqueueSnackbar(extractApiError(error, 'Error al guardar el turno'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (dialogsOpen) return;
      if (e.key === 'F2') {
        e.preventDefault();
        if (!saving && !soloLectura) formRef.current?.requestSubmit();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleCancel, saving, dialogsOpen, soloLectura]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <form ref={formRef} onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ textTransform: 'none' }}>
                  Turnos
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {isEdit ? (soloLectura ? 'Consulta de Turno' : 'Editando Turno') : 'Agregando Nuevo Turno'}
                </Typography>
              </Stack>

              {soloLectura && (
                <Alert severity="info">
                  Este turno ya fue facturado. Podés consultar los datos pero no modificarlos.
                </Alert>
              )}

              <Grid container spacing={2}>
                {/* Cliente + foto + estado */}
                <Grid item xs={12} md={9}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <TextField
                          label="Fecha"
                          type="date"
                          size="small"
                          value={fecha}
                          onChange={(e) => setFecha(e.target.value)}
                          disabled={soloLectura}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: todayIsoDate() }}
                          error={Boolean(fecha) && isFechaAnteriorAHoy(fecha)}
                          helperText={
                            fecha && isFechaAnteriorAHoy(fecha)
                              ? 'La fecha no puede ser anterior a hoy'
                              : undefined
                          }
                          sx={{ minWidth: 180 }}
                        />
                        <FormControl component="fieldset" disabled={soloLectura}>
                          <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>
                            Tipo Cliente
                          </FormLabel>
                          <RadioGroup
                            row
                            value={tipoCliente}
                            onChange={(e) => {
                              const next = e.target.value as TipoClienteTurno;
                              setTipoCliente(next);
                              if (next === 'Nuevo') {
                                setNroCliente(null);
                                setNombreCliente('');
                                setTelefonoCliente('');
                                setDireccionCliente('');
                                setLocalidadCliente(null);
                              }
                            }}
                          >
                            <FormControlLabel value="Nuevo" control={<Radio size="small" />} label="Nuevo" />
                            <FormControlLabel
                              value="Habitual"
                              control={<Radio size="small" />}
                              label="Habitual"
                            />
                          </RadioGroup>
                        </FormControl>
                        {isEdit && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={anulado}
                                onChange={(e) => setAnulado(e.target.checked)}
                                size="small"
                                disabled={soloLectura}
                              />
                            }
                            label="Anular Turno"
                          />
                        )}
                      </Stack>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                          <Stack spacing={1.5}>
                            {tipoCliente === 'Habitual' ? (
                              <Stack spacing={1.5}>
                                <TextField
                                  label="Cliente"
                                  size="small"
                                  fullWidth
                                  value={clienteDisplay}
                                  placeholder="Seleccionar cliente..."
                                  disabled={soloLectura}
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() => {
                                            if (!soloLectura) setOpenClienteSearch(true);
                                          }}
                                          edge="end"
                                          color="primary"
                                          aria-label="Buscar cliente"
                                          disabled={soloLectura}
                                        >
                                          <SearchIcon />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  onClick={() => {
                                    if (!soloLectura) setOpenClienteSearch(true);
                                  }}
                                  sx={{
                                    cursor: soloLectura ? 'default' : 'pointer',
                                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                  }}
                                />
                                {telefonoCliente ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Teléfono: {telefonoCliente}
                                  </Typography>
                                ) : null}
                              </Stack>
                            ) : (
                              <Stack spacing={1.5}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                  <TextField
                                    label="Nombre"
                                    size="small"
                                    fullWidth
                                    required
                                    value={nombreCliente}
                                    disabled={soloLectura}
                                    onChange={(e) => setNombreCliente(e.target.value)}
                                  />
                                  <TextField
                                    label="Teléfono"
                                    size="small"
                                    fullWidth
                                    value={telefonoCliente}
                                    disabled={soloLectura}
                                    onChange={(e) => setTelefonoCliente(e.target.value)}
                                  />
                                </Stack>
                                <TextField
                                  label="Dirección"
                                  size="small"
                                  fullWidth
                                  required
                                  value={direccionCliente}
                                  disabled={soloLectura}
                                  onChange={(e) => setDireccionCliente(e.target.value)}
                                  helperText="Se usa para dar de alta el cliente en el maestro"
                                />
                                <TextField
                                  label="Ciudad"
                                  size="small"
                                  fullWidth
                                  required
                                  value={localidadLabel}
                                  placeholder="Buscar localidad…"
                                  disabled={soloLectura}
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          edge="end"
                                          color="primary"
                                          onClick={() => {
                                            if (!soloLectura) setOpenLocalidadSearch(true);
                                          }}
                                          aria-label="Buscar ciudad"
                                          disabled={soloLectura}
                                        >
                                          <ArrowDropDownIcon />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                  onClick={() => {
                                    if (!soloLectura) setOpenLocalidadSearch(true);
                                  }}
                                  sx={{ cursor: soloLectura ? 'default' : 'pointer' }}
                                />
                              </Stack>
                            )}

                            <VendedorSelect
                              value={nroVendedor}
                              onChange={(nro) => setNroVendedor(nro)}
                              label="Vendedor"
                              size="small"
                              allowEmpty
                              emptyOptionLabel="Sin vendedor"
                              disabled={soloLectura}
                            />
                          </Stack>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Paper
                            variant="outlined"
                            sx={{
                              height: { xs: 160, md: '100%' },
                              minHeight: 160,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'grey.50',
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Foto
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Estado del Turno
                    </Typography>
                    <List dense disablePadding>
                      {ESTADOS_TURNO.map((est) => {
                        const selected = estado === est;
                        const yaFacturado = estado === 'FACTURADO';
                        const requiereOt = est === 'EN_TRABAJO';
                        const requiereFactura = est === 'FACTURADO';
                        const tieneOt = Boolean(nroFormularioOt && nroFormularioOt > 0);
                        // Facturado es terminal: no se puede volver a otro estado.
                        const permitido = yaFacturado
                          ? est === 'FACTURADO'
                          : (!requiereOt || tieneOt) && (!requiereFactura || yaFacturado);
                        const deshabilitado = !permitido;

                        return (
                          <ListItemButton
                            key={est}
                            selected={selected}
                            disabled={deshabilitado}
                            onClick={() => {
                              if (!permitido) {
                                if (yaFacturado) {
                                  enqueueSnackbar(
                                    'Un turno facturado no puede cambiar de estado',
                                    { variant: 'warning' },
                                  );
                                } else if (requiereOt) {
                                  enqueueSnackbar(
                                    'Generá la orden de trabajo para pasar a En trabajo',
                                    { variant: 'warning' },
                                  );
                                } else if (requiereFactura) {
                                  enqueueSnackbar(
                                    'Facturado se asigna al facturar la orden de trabajo',
                                    { variant: 'warning' },
                                  );
                                }
                                return;
                              }
                              setEstado(est);
                            }}
                            sx={{
                              borderRadius: 1,
                              mb: 0.25,
                              border: '1px solid',
                              borderColor: selected ? BORDER_ESTADO_TURNO[est] : 'transparent',
                              bgcolor: selected ? COLOR_ESTADO_TURNO[est] : 'transparent',
                              transition: 'background-color 0.15s, border-color 0.15s, filter 0.15s',
                              opacity: deshabilitado && !selected ? 0.45 : 1,
                              '&.Mui-disabled': {
                                opacity: selected ? 1 : 0.45,
                              },
                              '&:hover': {
                                bgcolor: deshabilitado ? undefined : COLOR_ESTADO_TURNO[est],
                                borderColor: deshabilitado ? undefined : BORDER_ESTADO_TURNO[est],
                                filter: selected && !deshabilitado ? 'brightness(0.96)' : 'none',
                              },
                              '&.Mui-selected': {
                                bgcolor: COLOR_ESTADO_TURNO[est],
                              },
                              '&.Mui-selected:hover': {
                                bgcolor: COLOR_ESTADO_TURNO[est],
                                filter: deshabilitado ? 'none' : 'brightness(0.96)',
                              },
                            }}
                          >
                            <ListItemText
                              primary={LABEL_ESTADO_TURNO[est]}
                              secondary={
                                deshabilitado && !selected
                                  ? yaFacturado
                                    ? 'Turno facturado'
                                    : requiereOt
                                      ? 'Requiere OT'
                                      : requiereFactura
                                        ? 'Solo al facturar'
                                        : undefined
                                  : undefined
                              }
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Paper>
                </Grid>

                {/* Servicio / tiempos */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                          select
                          label="Servicio"
                          size="small"
                          fullWidth
                          value={codigoServicio}
                          onChange={(e) => setCodigoServicio(e.target.value)}
                          disabled={loadingServicios || soloLectura}
                          helperText={
                            !loadingServicios && servicios.length === 0
                              ? 'Sin servicios disponibles (el catálogo aún no responde)'
                              : undefined
                          }
                        >
                          {servicios.map((s) => (
                            <MenuItem key={s.codigo} value={s.codigo}>
                              {s.descripcion || s.codigo}
                            </MenuItem>
                          ))}
                        </TextField>
                        <IconButton
                          onClick={() => setOpenServiciosCrud(true)}
                          color="primary"
                          aria-label="Administrar servicios"
                          disabled={soloLectura}
                          sx={{ mt: 0.25 }}
                        >
                          <SearchIcon />
                        </IconButton>
                      </Stack>

                      <TextField
                        label="Unidad"
                        size="small"
                        fullWidth
                        value={patente}
                        placeholder="Unidad a la que se dará servicio"
                        disabled={soloLectura}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <DirectionsCarIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              {patente && !soloLectura ? (
                                <IconButton
                                  size="small"
                                  aria-label="Quitar unidad"
                                  onClick={() => setPatente('')}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              ) : null}
                              <IconButton
                                onClick={() => {
                                  if (soloLectura) return;
                                  if (!nroCliente) {
                                    enqueueSnackbar('Seleccioná primero un cliente', { variant: 'warning' });
                                    return;
                                  }
                                  setOpenUnidadSearch(true);
                                }}
                                edge="end"
                                color="primary"
                                aria-label="Buscar unidad"
                                disabled={soloLectura}
                              >
                                <SearchIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        onClick={() => {
                          if (soloLectura) return;
                          if (!nroCliente) {
                            enqueueSnackbar('Seleccioná primero un cliente', { variant: 'warning' });
                            return;
                          }
                          setOpenUnidadSearch(true);
                        }}
                        sx={{
                          cursor: soloLectura ? 'default' : 'pointer',
                          '& .MuiOutlinedInput-root': { borderRadius: 2 },
                        }}
                      />

                      <HorarioTurno
                        horaInicio={horaInicio}
                        onHoraInicioChange={setHoraInicio}
                        duracionMinutos={duracionMinutos}
                        onDuracionChange={setDuracionMinutos}
                        horaFinal={horaFinal}
                        disabled={soloLectura}
                      />
                    </Stack>
                  </Paper>
                </Grid>

                {/* Nota */}
                <Grid item xs={12}>
                  <TextField
                    label="Nota"
                    multiline
                    minRows={4}
                    fullWidth
                    value={nota}
                    disabled={soloLectura}
                    onChange={(e) => setNota(e.target.value)}
                  />
                </Grid>
              </Grid>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                sx={{ pt: 1 }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {nroFormularioOt ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<BuildIcon />}
                      onClick={() => navigate(ROUTES.servicio.ordenesTrabajo.editar(nroFormularioOt))}
                      sx={{ textTransform: 'none' }}
                    >
                      Abrir OT #{nroFormularioOt}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={
                        generandoOt ? <CircularProgress size={16} color="inherit" /> : <BuildIcon />
                      }
                      onClick={() => void handleGenerarOt()}
                      disabled={soloLectura || !isEdit || generandoOt || saving || !nroCliente}
                      sx={{ textTransform: 'none' }}
                    >
                      Generar orden de trabajo
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PaymentIcon />}
                    onClick={handleRegistrarSena}
                    disabled={soloLectura || !nroCliente || nroCliente <= 0}
                    sx={{ textTransform: 'none' }}
                  >
                    Registrar seña
                  </Button>
                  <Can permiso="ventas.facturas.nuevo">
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<ReceiptLongIcon />}
                    onClick={handleFacturarOt}
                    disabled={soloLectura || !nroFormularioOt}
                    sx={{ textTransform: 'none' }}
                  >
                    Facturar
                  </Button>
                  </Can>
                </Stack>

                <Stack direction="row" spacing={2}>
                  {!soloLectura && (
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving}
                      sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, textTransform: 'none' }}
                    >
                      {saving ? <CircularProgress size={24} /> : 'F2-Aceptar'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant={soloLectura ? 'contained' : 'outlined'}
                    onClick={handleCancel}
                    disabled={saving}
                    sx={{ textTransform: 'none' }}
                  >
                    {soloLectura ? 'Volver' : 'ESC-Cancelar'}
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ClienteSearch
        open={openClienteSearch}
        onClose={() => setOpenClienteSearch(false)}
        onSelect={handleClienteSearchSelect}
      />
      <LocalidadSearch
        open={openLocalidadSearch}
        onClose={() => setOpenLocalidadSearch(false)}
        onSelect={(loc) => {
          setLocalidadCliente({
            codigoCiudad: loc.codigoCiudad,
            nombreCiudad: loc.nombreCiudad,
            nombreProvincia: loc.nombreProvincia,
          });
          setOpenLocalidadSearch(false);
        }}
      />

      <CatalogoCrudDialog
        open={openServiciosCrud}
        onClose={handleCloseServiciosCrud}
        onSelect={(item) => {
          setCodigoServicio(item.codigo);
          handleCloseServiciosCrud();
        }}
        title="Servicios"
        codigoLabel="Código"
        nombreLabel="Descripción"
        list={(term) => turnoService.getServicios(term || undefined)}
        create={(codigo, descripcion) => turnoService.createServicio({ codigo, descripcion })}
        update={(codigo, descripcion) => turnoService.updateServicio(codigo, { descripcion })}
        remove={(codigo) => turnoService.deleteServicio(codigo)}
      />

      <UnidadCrudDialog
        open={openUnidadSearch}
        onClose={() => setOpenUnidadSearch(false)}
        onSelect={(item) => {
          setPatente(item.codigo);
          setOpenUnidadSearch(false);
        }}
        nroCliente={nroCliente ?? 0}
      />
    </Box>
  );
};

export default TurnoForm;
